import BaseObject from 'app/src/base/BaseObject';
import Register from 'app/src/data/models/Register';
import RegisterCollection from 'app/src/data/models/RegisterCollection';
import Shop from 'app/src/data/models/Shop';
import ShopCollection from 'app/src/data/models/ShopCollection';
import Filter from 'app/src/data/models/utils/Filter';

let _shops = null;
let _registers = null;
let _selectedShop = null;

const FETCH_LIMIT = 100;

/**
 * Class to provide a container for commonly used data
 */
export default class CommonData extends BaseObject {
  /**
   * Initializer for the CommonData module.  This module will be initialized when
   * the application starts.
   *
   * @returns {jQuery.Promise}
   */
  static init() {
    let promise = CommonData._getAllShops();

    return promise.done(() => {
      CommonData.selectedShop = _shops.findWhere(
        (shop) => shop.id == window.merchantos.session.getShop().shopID
      );
      _registers = {};

      _shops.forEach((shop) => {
        _registers[shop.id] = shop.getRelation('Registers', RegisterCollection);
      });
    });
  }

  /**
   * Returns a promise the resolves once all shops have been fetched from the server.
   * The shops collection is stored in the private _shops collection.
   *
   * @return {jQuery.promise} The jQuery promise
   */
  static _getAllShops() {
    let deferred = $.Deferred();
    _shops = new ShopCollection();

    CommonData._getShops(0, deferred);

    return deferred.promise();
  }

  /**
   * Recursively fetches all the shops from the server.  Necessary since the server will
   * limit the maximum number of result per request to 100.
   *
   * @param {Number} offset The offset of the fetch
   * @param {jQuery.Deferred} deferred Resolved when the shops have finished being retrieved.
   */
  static _getShops(offset, deferred) {
    let shops = new ShopCollection();
    let filter = new Filter()
      .exactly('limit', FETCH_LIMIT)
      .exactly('offset', offset)
      .loadRelations('all');

    shops
      .fetch(filter)
      .done(() => {
        _shops.add(shops.models, { merge: true });

        if (offset + shops.length < shops.recordCount) {
          setTimeout(CommonData._getShops, 0, offset + shops.length, deferred);
        } else {
          deferred.resolve();
        }
      })
      .fail((error) => {
        deferred.reject(error);
      });
  }

  /**
   * Exposes CommonData events through the class.
   */
  static get Events() {
    return _Events;
  }

  /**
   * Exposes the CommonData event channel.  Listeners of this channel will be notified
   * whenever a CommonData event occurs.
   *
   * @returns {*} The event channel.
   */
  static get channel() {
    return _commonDataChannel;
  }

  /**
   * Setter for the selected shop.
   *
   * @param shop {Shop}   The shop model for the newly selected shop.
   */
  static set selectedShop(shop) {
    _selectedShop = shop;
  }

  /**
   * Getter for the selected shop in the current session
   *
   * @returns {Shop}  A model for the currently selected shop.
   */
  static get selectedShop() {
    return _selectedShop;
  }

  /**
   * Getter for the selected register in the current session
   *
   * @returns {Register}  A model for the currently selected register or null.
   */
  static get selectedRegister() {
    let selectedRegister = null;

    let registersForShop = CommonData.getRegistersForShop(
      CommonData.selectedShop ? CommonData.selectedShop.id : null
    );
    if (registersForShop) {
      // eslint-disable-next-line max-len
      selectedRegister =
        registersForShop.findWhere(
          (register) => register.id == window.merchantos.session.getRegister().registerID
        ) || null;
    }

    return selectedRegister;
  }

  /**
   * Retrieves a list of the available shops.
   *
   * @returns {ShopCollection}
   */
  static get shops() {
    return _shops;
  }

  /**
   * Retrieves a collection of the available register for the specified shop.
   *
   * @param shopID {Number}  The shop ID
   * @returns {RegisterCollection}
   */
  static getRegistersForShop(shopID) {
    let registers = null;

    if (_registers) {
      registers = _registers[shopID] || null;
    }

    return registers;
  }
}
